import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/utils/analytics";
import { CrewFigure } from "./crewFigures";
import { crewFor, crewLine, type CrewMode } from "./crewData";
import { quoted, SK_WORLDS, type WorldsContent } from "./worldsContent";
import "./worlds.css";

const INTERVAL_MS = 2600;

/**
 * Rotates on its own while visible. Pauses while a mouse is over the row or a
 * figure has keyboard focus; a tap on a touch screen stops it for good.
 */
function useCrewRotation(count: number) {
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true);
  const [visible, setVisible] = useState(false);
  const [held, setHeld] = useState(false);
  const [tick, setTick] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const pointer = useRef<string>("mouse");

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) setAuto(false);
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.4 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!auto || !visible || held) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
      setTick((t) => t + 1);
    }, INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [auto, visible, held, count]);

  const pick = (next: number) => {
    setIndex(next);
    setTick((t) => t + 1);
    if (pointer.current !== "mouse") setAuto(false);
  };
  const rowHandlers = {
    onPointerDown: (e: { pointerType: string }) => { pointer.current = e.pointerType; },
    onPointerEnter: (e: { pointerType: string }) => { if (e.pointerType === "mouse") setHeld(true); },
    onPointerLeave: (e: { pointerType: string }) => { if (e.pointerType === "mouse") setHeld(false); },
    onFocus: () => setHeld(true),
    onBlur: () => setHeld(false),
  };
  return { ref, index, tick, pick, rowHandlers };
}

/** Where a figure stands relative to the selected one, for the narrow-screen carousel. */
function slotPosition(i: number, index: number, count: number): string {
  let offset = (i - index + count) % count;
  if (offset > count / 2) offset -= count;
  if (offset === 0) return "on";
  if (Math.abs(offset) > 2) return "off";
  return `${offset < 0 ? "prev" : "next"}${Math.abs(offset) === 2 ? "2" : ""}`;
}

function bubbleAlign(index: number, count: number): string {
  if (index < 2) return "left-0";
  if (index > count - 3) return "right-0";
  return "left-1/2 -translate-x-1/2";
}

/**
 * The twelve figures with their lines. In "mix" mode (home page) each figure
 * speaks for its own world and a link under the row leads to that world's page.
 */
export function CrewLineup({ mode, content = SK_WORLDS }: { mode: CrewMode; content?: WorldsContent }) {
  const crew = crewFor(mode, content);
  const count = crew.length;
  const { ref, index, tick, pick, rowHandlers } = useCrewRotation(count);
  const world = mode === "mix" ? content.crew[crew[index]].home : mode;
  const page = content.pages[world];

  return (
    <div ref={ref} className="mx-auto w-full max-w-[80rem] lg:ml-[calc(50%-min(60rem,50vw-2rem))] lg:w-[min(120rem,calc(100vw-4rem))] lg:max-w-none">
      <div data-crew-row role="group" aria-label={content.ui.lineupAria} {...rowHandlers} className="flex items-end justify-center gap-1 overflow-hidden px-2 pt-36 lg:justify-between lg:overflow-visible lg:px-6">
        {crew.map((key, i) => {
          const on = i === index;
          const pos = slotPosition(i, index, count);
          return (
            <button
              key={key}
              type="button"
              aria-pressed={on}
              data-pos={pos}
              onClick={() => {
                pick(i);
                trackEvent("crew_figure_click", "worlds", key, undefined, { mode });
              }}
              className={`crew-slot relative flex flex-none cursor-pointer flex-col items-center gap-3 rounded-2xl px-0.5 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${on ? "-translate-y-3 scale-105 opacity-100" : "opacity-60 hover:opacity-100"}`}
            >
              {on ? (
                <span className={`crew-bubble-wrap absolute bottom-full mb-1 w-64 ${bubbleAlign(i, count)}`}>
                  <span key={tick} className="crew-bubble block rounded-2xl rounded-bl-sm bg-foreground px-4 py-3 text-left text-base font-semibold leading-snug text-background shadow-xl">
                    {quoted(crewLine(key, mode, content.crew).quote, content.quotes)}
                  </span>
                </span>
              ) : null}
              <CrewFigure crew={key} />
              <span className={`crew-name whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ${on ? "bg-primary text-primary-foreground" : "bg-background/80 text-foreground"}`}>
                {content.crew[key].label}
              </span>
            </button>
          );
        })}
      </div>
      <div aria-hidden="true" className="-mt-1.5 h-4 rounded-full bg-primary/15" />

      {mode === "mix" ? (
        <div className="flex justify-center pb-2 pt-6">
          <Button asChild variant={world === "survive" ? "default" : "outline"} size="lg" className="w-full sm:w-auto">
            <Link to={page.href} onClick={() => trackEvent("world_door_click", "worlds", world, undefined, { source: "crew_lineup", figure: crew[index] })}>
              {page.door}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
